import React, { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../hooks/useToast';
import { authService, stockService, type StockBalance } from '../services';

interface BalanceFilters {
  itemId: string;
}

type ExpandedMap = Record<string, boolean>;

const StockBalancesPage: React.FC = () => {
  const userRole = authService.getUserRole();
  const { showError } = useToast();

  const [balances, setBalances] = useState<StockBalance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<BalanceFilters>({ itemId: '' });
  const debouncedItemId = useDebounce(filters.itemId, 300);
  const [cnpjFilter, setCnpjFilter] = useState('');
  const canFilterByCnpj = userRole === 'DEVELOPER';

  const [expanded, setExpanded] = useState<ExpandedMap>({});

  const loadBalances = useCallback(async () => {
    try {
      setLoading(true);
      const response = await stockService.listStockBalances({
        cnpj: canFilterByCnpj ? cnpjFilter.trim() || undefined : undefined,
      });

      const filtered = response.filter((balance) => {
        if (debouncedItemId && !balance.itemId.toLowerCase().includes(debouncedItemId.toLowerCase())) {
          return false;
        }
        return true;
      });

      setBalances(filtered);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar saldos de estoque');
    } finally {
      setLoading(false);
    }
  }, [canFilterByCnpj, cnpjFilter, debouncedItemId]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  const summary = useMemo(() => {
    return balances.reduce(
      (acc, balance) => {
        acc.totalQuantity += balance.quantity;
        acc.totalAvailable += balance.availableQuantity ?? balance.quantity;
        acc.items += 1;
        return acc;
      },
      { totalQuantity: 0, totalAvailable: 0, items: 0 },
    );
  }, [balances]);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilters({ itemId: event.target.value });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2, px: { xs: 1, sm: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Saldos de Estoque
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Acompanhe o estoque disponível por item, incluindo reservas e camadas FIFO.
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: 2, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Resumo atual
          </Typography>
          <Typography variant="subtitle1">{summary.items} itens</Typography>
          <Box display="flex" gap={3}>
            <Typography variant="body2" color="text.secondary">
              Total físico <br />
              <strong>{summary.totalQuantity.toLocaleString('pt-BR')}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Disponível <br />
              <strong>{summary.totalAvailable.toLocaleString('pt-BR')}</strong>
            </Typography>
          </Box>
        </Paper>
      </Box>

      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="flex-start">
          <TextField
            label="Filtrar por ID do item"
            value={filters.itemId}
            onChange={handleFilterChange}
            size="small"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Chip label={balances.length} size="small" color="primary" variant="outlined" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', md: 320 } }}
          />

          {canFilterByCnpj && (
            <TextField
              label="CNPJ"
              value={cnpjFilter}
              onChange={(event: ChangeEvent<HTMLInputElement>) => setCnpjFilter(event.target.value)}
              size="small"
              placeholder="Somente números"
              sx={{ width: { xs: '100%', md: 220 } }}
            />
          )}

          <Box display="flex" gap={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadBalances}>
              Atualizar
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={1}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : balances.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <Typography variant="body2" color="text.secondary">
              Nenhum saldo encontrado para os filtros informados.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={48} />
                  <TableCell>Item</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell align="right">Saldo físico</TableCell>
                  <TableCell align="right">Reservado</TableCell>
                  <TableCell align="right">Disponível</TableCell>
                  <TableCell align="right">Custo médio</TableCell>
                  <TableCell>Última movimentação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balances.map((balance) => {
                  const key = `${balance.itemId}-${balance.unitId || 'default'}`;
                  const available = balance.availableQuantity ?? balance.quantity - (balance.reservedQuantity ?? 0);
                  const isNegative = available < 0;

                  return (
                    <React.Fragment key={key}>
                      <TableRow hover>
                        <TableCell width={48}>
                          {balance.fifoLayers && balance.fifoLayers.length > 0 ? (
                            <IconButton size="small" onClick={() => toggleExpanded(key)}>
                              <ExpandMoreIcon
                                sx={{
                                  transform: expanded[key] ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s ease',
                                }}
                              />
                            </IconButton>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column">
                            <Typography variant="subtitle2">{balance.itemId}</Typography>
                            {balance.unitId && (
                              <Typography variant="caption" color="text.secondary">
                                Unidade: {balance.unitId}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{balance.unit}</TableCell>
                        <TableCell align="right">{balance.quantity.toLocaleString('pt-BR')}</TableCell>
                        <TableCell align="right">
                          {(balance.reservedQuantity ?? 0).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            color={isNegative ? 'error.main' : 'text.primary'}
                            fontWeight={isNegative ? 600 : 400}
                          >
                            {available.toLocaleString('pt-BR')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {balance.averageCost !== undefined
                            ? balance.averageCost.toLocaleString('pt-BR', {
                                style: 'currency',
                                currency: 'BRL',
                              })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {balance.lastMovementAt
                            ? new Intl.DateTimeFormat('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              }).format(new Date(balance.lastMovementAt))
                            : '-'}
                        </TableCell>
                      </TableRow>
                      {balance.fifoLayers && balance.fifoLayers.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: 0 }}>
                            <Collapse in={!!expanded[key]} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 3, py: 2, bgcolor: 'background.default' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                  Camadas FIFO
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Lote</TableCell>
                                      <TableCell align="right">Quantidade</TableCell>
                                      <TableCell align="right">Custo unitário</TableCell>
                                      <TableCell>Recebido em</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {balance.fifoLayers.map((layer) => (
                                      <TableRow key={layer.lotId}>
                                        <TableCell>{layer.lotId}</TableCell>
                                        <TableCell align="right">
                                          {layer.quantity.toLocaleString('pt-BR')}
                                        </TableCell>
                                        <TableCell align="right">
                                          {layer.unitCost.toLocaleString('pt-BR', {
                                            style: 'currency',
                                            currency: 'BRL',
                                          })}
                                        </TableCell>
                                        <TableCell>
                                          {layer.receivedAt
                                            ? new Intl.DateTimeFormat('pt-BR').format(new Date(layer.receivedAt))
                                            : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {canFilterByCnpj && (
        <Box mt={2}>
          <Alert severity="info">
            Filtrando saldos para o CNPJ <strong>{cnpjFilter.trim() || 'padrão do usuário'}</strong>.
          </Alert>
        </Box>
      )}

      {!loading && balances.some((balance) => (balance.availableQuantity ?? balance.quantity) < 0) && (
        <Box mt={2}>
          <Alert severity="warning" onClose={() => showError('Saldo negativo identificado.')}>/!
            Há itens com saldo disponível negativo. Verifique inventários pendentes ou lançamentos incorretos.
          </Alert>
        </Box>
      )}
    </Container>
  );
};

export default StockBalancesPage;
